// src/context/AlertContext.js
import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";
import MDSnackbar from "components/MDSnackbar";
import Icon from "@mui/material/Icon";

// Create context
const SnackbarContext = createContext({
  showSnackbar: () => {},
});

let idCounter = 0;

export const SnackbarProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showSnackbar = useCallback(
    ({
      title = "",
      content = "",
      color = "info",
      duration = 5000,
      icon = <Icon>notifications</Icon>,
      bgWhite = false,
      dateTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    } = {}) => {
      const id = ++idCounter;
      setToasts((prev) => [
        ...prev,
        {
          id,
          title,
          content,
          color,
          duration,
          icon,
          bgWhite,
          dateTime,
        },
      ]);
      // auto-remove after duration + small buffer
      setTimeout(() => removeToast(id), duration + 200);
    },
    [removeToast]
  );

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {toasts.map(({ id, title, content, color, duration, icon, bgWhite, dateTime }) => (
        <MDSnackbar
          key={id}
          open
          title={title}
          content={content}
          color={color}
          icon={icon}
          dateTime={dateTime}
          bgWhite={bgWhite}
          // Pass a close handler to remove this toast
          close={() => removeToast(id)}
          // Keep the autoHideDuration in sync (optional, MDSnackbar has its own)
          autoHideDuration={duration}
          // You can spread other props if needed
        />
      ))}
    </SnackbarContext.Provider>
  );
};

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use snackbar
export const useSnackbar = () => useContext(SnackbarContext);
